import { useQuery } from '@apollo/client/react'
import { ScoContentPermissions, ScoUser } from '@src/graphql-types'
import { useCurrentUser } from '@src/hooks/useCurrentUser'
import { gql } from '@src/lib/gql'

export type UserAccessRules = {
  contentPermissions?: Array<keyof ScoContentPermissions>
  isRegistered?: boolean
  viewLimit?: {
    maximumCount?: number
    minimumCount?: number
    minimumCompletionAsPercent?: number
    parentId?: string
    parentType?: string
    viewableId?: string
    viewableType?: string
  }
}

export type UserAccessCheckOptions = {
  bypass?: UserAccessRules | boolean
  require?: UserAccessRules | boolean
}

export type UserAccessCheck = {
  accessCheckPassed?: boolean
  currentUser?: ScoUser
  nextStepHref?: string
}

export function useUserAccessCheck(options: UserAccessCheckOptions): UserAccessCheck {
  const { currentUser } = useCurrentUser()

  const bypassRules = (options.bypass as UserAccessRules) || {}

  const requireRules = (options.require as UserAccessRules) || {}

  const bypassViewsQuery = useQuery(VIEWS_QUERY, {
    skip: options.bypass === true || !bypassRules.viewLimit,
    fetchPolicy: 'network-only',
    variables: {
      ...(bypassRules.viewLimit || {}),
      userId: currentUser?.userId,
    },
  })
  const requireViewsQuery = useQuery(VIEWS_QUERY, {
    skip: options.bypass === true || !requireRules.viewLimit,
    fetchPolicy: 'network-only',
    variables: {
      ...(requireRules.viewLimit || {}),
      userId: currentUser?.userId,
    },
  })

  const result: UserAccessCheck = {
    accessCheckPassed: false,
    currentUser,
  }

  if (!currentUser) {
    return result
  }

  if (options.bypass) {
    if (options.bypass === true) {
      result.accessCheckPassed = true
    } else {
      if (bypassRules.isRegistered) {
        if (currentUser.isRegistered) {
          result.accessCheckPassed = true
        }
      }

      if (bypassRules.contentPermissions?.length > 0) {
        if (
          bypassRules.contentPermissions.filter(
            permissionName => currentUser.contentPermissions[permissionName]
          ).length === bypassRules.contentPermissions?.length
        ) {
          result.accessCheckPassed = true
        }
      }

      if (bypassRules.viewLimit) {
        const bypassViewCount = bypassViewsQuery.data?.scoViews?.pagination?.itemCount
        if (
          bypassRules.viewLimit.maximumCount &&
          bypassViewCount != null &&
          bypassViewCount <= bypassRules.viewLimit.maximumCount
        ) {
          result.accessCheckPassed = true
        } else if (
          bypassRules.viewLimit.minimumCount &&
          bypassViewCount != null &&
          bypassViewCount >= bypassRules.viewLimit.minimumCount
        ) {
          result.accessCheckPassed = true
        }
      }
    }
  }

  if (result.accessCheckPassed) {
    return result
  }

  if (options.bypass !== true && options.require) {
    if (options.require === true) {
      result.accessCheckPassed = false
      return result
    } else {
      if (requireRules.isRegistered) {
        if (currentUser?.isRegistered) {
          result.accessCheckPassed = true
        }
      }

      if (requireRules.contentPermissions?.length > 0) {
        if (
          requireRules.contentPermissions.filter(
            permissionName => currentUser.contentPermissions[permissionName]
          ).length === requireRules.contentPermissions?.length
        ) {
          result.accessCheckPassed = true
        }
      }

      if (requireRules.viewLimit) {
        if (
          requireRules.viewLimit.maximumCount &&
          requireViewsQuery.data?.scoViews?.pagination?.itemCount <= requireRules.viewLimit.maximumCount
        ) {
          result.accessCheckPassed = true
        } else if (
          requireRules.viewLimit.minimumCount &&
          requireViewsQuery.data?.scoViews?.pagination?.itemCount >= requireRules.viewLimit.minimumCount
        ) {
          result.accessCheckPassed = true
        }
      }
    }
  }

  return result
}

const VIEWS_QUERY = gql`
  query ($viewableType: String, $viewableId: String, $minimumCompletionAsPercent: Int) {
    scoViews(
      viewableType: $viewableType
      viewableId: $viewableId
      minimumCompletionAsPercent: $minimumCompletionAsPercent
    ) {
      id
      pagination {
        itemCount
      }
    }
  }
`
